import React, { useState } from 'react';
import { Box, Typography, Paper, Tooltip, Chip, IconButton, Collapse } from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BlockIcon from '@mui/icons-material/Block';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EmailIcon from '@mui/icons-material/Email';

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const MetricsInfo = () => {
  const metrics = [
    {
      title: 'Dropout Rate',
      description: 'The percentage of members who were present in the previous file but are no longer included in the latest file.',
      example: 'Example: If 100 members were present in the previous file and 12 are missing in the current file, the dropout rate is 12%.'
    },
    {
      title: 'Termination Rate',
      description: 'The percentage of members whose coverage was terminated due to eligibility issues, non-payment, or other administrative reasons.',
      example: 'Example: If 100 members are enrolled and 8 have their coverage terminated, the termination rate is 8%.'
    },
    {
      title: 'Fallout Rate',
      description: 'The percentage of members who were active in the previous file but are missing in the current file without any documented reason for termination or dropout.',
      example: 'Example: If 100 members were active in the previous file and 5 are missing without clear termination records, the fallout rate is 5%.'
    },
    {
      title: 'Fill Rate',
      description: 'The proportion of records that have contact information such as phone number, email, or mailing address.',
      example: 'Example: If 100 records exist and 90 have phone numbers, the phone fill rate is 90%.'
    },
    {
      title: 'Valid Rate',
      description: 'The percentage of contact fields (mail, phone, address) that are valid and properly formatted.',
      example: 'Example: If 85 out of 100 phone numbers are valid, the valid rate is 85%.'
    },
    {
        title: 'Active / Terminated Members',
        description: 'Represents the count of members who are currently active in the program versus those whose participation has ended due to termination. This helps understand the current program population and recent churn.',
        example: 'Example: Out of 150 total members, 110 are active and 40 are terminated.'
      }
         ,
    {
      title: 'Data Quality Metrics',
      description: 'Summarizes record-level quality issues like clean, warning, error, and critical records found in the latest file.',
      example: 'Example: Clean Records: 75, Warning: 10, Error: 12, Critical: 3'
    }
  ];

  const [expanded, setExpanded] = useState(false);
  const toggleExpand = () => setExpanded(!expanded);

  const getMetricIcon = (title) => {
    switch (title) {
      case 'Dropout Rate':
        return <TrendingDownIcon fontSize="small" sx={{ color: '#f44336' }} />;
      case 'Termination Rate':
        return <BlockIcon fontSize="small" sx={{ color: '#2e7d32' }} />;
      case 'Fallout Rate':
        return <ErrorOutlineIcon fontSize="small" sx={{ color: '#ff9800' }} />;
      case 'Fill Rate':
        return <EmailIcon fontSize="small" sx={{ color: '#1976d2' }} />;
      case 'Valid Rate':
        return <VerifiedUserIcon fontSize="small" sx={{ color: '#43a047' }} />;
      case 'Active Member Rate':
        return <GroupIcon fontSize="small" sx={{ color: '#5c6bc0' }} />;
      case 'Data Quality Metrics':
        return <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />;
      default:
        return <InfoIcon fontSize="small" sx={{ color: '#2196f3' }} />;
    }
  };

  return (
    <Box sx={{ mt: 4, mb: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: '#fafafa',
          border: '1px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
            Key Metrics Glossary
          </Typography>
          <IconButton
            size="small"
            onClick={toggleExpand}
            aria-expanded={expanded}
            aria-label="show more"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {metrics.map((metric, index) => (
  <Box
    key={index}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      p: 1,
      borderRadius: 1,
      backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
    }}
  >
    {/* Fixed-width icon column */}
    <Box sx={{ width: 32, flexShrink: 0, mt: 0.5 }}>
      {getMetricIcon(metric.title)}
    </Box>

    {/* Full text column */}
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {metric.title}
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {metric.description}
      </Typography>

      <Tooltip title={metric.example} arrow placement="bottom-start">
        <Chip
          label="Example"
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
        />
      </Tooltip>
    </Box>
  </Box>
))}

          </Box>
        </Collapse>

        {!expanded && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
            Click to expand for metric definitions
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MetricsInfo;
